import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { trackPageView } from '@dfq/shared/hooks/useAnalytics';

/**
 * SF-INFRA-003 — GA4 page-view tracker.
 *
 * A zero-render component that hooks into React Router's `useLocation` and
 * fires `trackPageView(location.pathname, document.title)` on every
 * navigation (and on the initial mount). MUST be rendered inside the
 * `<RouterProvider>` tree — e.g. as a child of a layout element referenced
 * from the router config — so `useLocation` has a router context.
 *
 * Returns `null`: all side-effects are analytics calls.
 *
 * The `trackPageView` helper itself is guarded against DEV mode and a
 * missing `window.gtag`, so this component is safe to mount unconditionally
 * — it simply no-ops in environments where GA4 hasn't been initialised.
 */
export function Analytics(): null {
  const location = useLocation();

  useEffect(() => {
    const title =
      typeof document !== 'undefined' ? document.title : undefined;
    trackPageView(location.pathname + location.search, title);
  }, [location.pathname, location.search]);

  return null;
}
